import { Component } from '@theme/component';
import {
  getScrollContainer,
  getScrollEventTarget,
  getScrollTop,
  scrollContainerMediaQuery,
  scrollTo,
} from '@theme/scroll-container';
import { prefersReducedMotion } from '@theme/utilities';

/** Horizontal breathing room (px) used when checking overlap with the sticky add-to-cart bar. */
const OVERLAP_MARGIN = 12;

/**
 * @typedef {object} ScrollToTopRefs
 * @property {HTMLButtonElement} button - The floating button
 */

/**
 * A floating "back to top" button.
 *
 * - Appears once the page scroll container passes `data-show-after` pixels.
 * - Scrolls smoothly to the top on click (instantly when reduced motion is preferred).
 * - Lifts above Horizon's sticky add-to-cart bar while that bar is visible and overlaps it.
 *
 * @extends {Component<ScrollToTopRefs>}
 */
class ScrollToTopComponent extends Component {
  requiredRefs = ['button'];

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {EventTarget | null} */
  #scrollTarget = null;

  /** @type {number | null} */
  #scrollRafId = null;

  /** @type {number | null} */
  #layoutRafId = null;

  /** @type {boolean | null} */
  #visible = null;

  /** @type {boolean} */
  #editorSelected = false;

  /** @type {HTMLElement[]} */
  #stickyBars = [];

  /** @type {MutationObserver | null} */
  #barMutationObserver = null;

  /** @type {ResizeObserver | null} */
  #barResizeObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#bindScrollTarget();
    scrollContainerMediaQuery.addEventListener('change', this.#handleBreakpointChange, { signal });
    window.addEventListener('resize', this.#scheduleLayoutUpdate, { passive: true, signal });

    // Theme editor: reveal the button while its section is selected, and re-watch the
    // sticky bar when the product section is re-rendered.
    document.addEventListener('shopify:section:select', this.#handleSectionSelect, { signal });
    document.addEventListener('shopify:section:deselect', this.#handleSectionDeselect, { signal });
    document.addEventListener('shopify:section:load', this.#handleSectionLoad, { signal });
    document.addEventListener('shopify:section:unload', this.#scheduleStickyBarWatch, { signal });

    this.#watchStickyBars();
    this.#updateVisibility();
    this.#updateLayout();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController?.abort();
    this.#abortController = null;
    this.#scrollTarget?.removeEventListener('scroll', this.#handleScroll);
    this.#scrollTarget = null;
    this.#barMutationObserver?.disconnect();
    this.#barResizeObserver?.disconnect();

    if (this.#scrollRafId !== null) cancelAnimationFrame(this.#scrollRafId);
    if (this.#layoutRafId !== null) cancelAnimationFrame(this.#layoutRafId);
    this.#scrollRafId = null;
    this.#layoutRafId = null;
  }

  /**
   * Scrolls the page back to the top. Bound declaratively via `on:click="/scrollToTop"`.
   * @param {MouseEvent} [event]
   */
  scrollToTop(event) {
    scrollTo({ top: 0, behavior: prefersReducedMotion() ? 'instant' : 'smooth' });

    // Keyboard activation (detail === 0): the button hides once we reach the top, so move
    // focus to the start of the page to keep the tab order where the shopper now is.
    if (event?.detail === 0) {
      const start = /** @type {HTMLElement | null} */ (
        document.querySelector('.skip-to-content-link, header a[href], header button')
      );
      start?.focus({ preventScroll: true });
    }
  }

  get #showAfter() {
    const value = Number.parseInt(this.dataset.showAfter ?? '', 10);
    return Number.isFinite(value) ? value : 400;
  }

  #bindScrollTarget() {
    this.#scrollTarget?.removeEventListener('scroll', this.#handleScroll);
    this.#scrollTarget = getScrollEventTarget();
    this.#scrollTarget.addEventListener('scroll', this.#handleScroll, { passive: true });
  }

  #handleBreakpointChange = () => {
    this.#bindScrollTarget();
    this.#updateVisibility();
    this.#scheduleLayoutUpdate();
  };

  #handleScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateVisibility();
    });
  };

  #updateVisibility() {
    const visible = this.#editorSelected || getScrollTop() > this.#showAfter;
    if (visible === this.#visible) return;

    this.#visible = visible;
    this.dataset.visible = String(visible);
  }

  #scheduleLayoutUpdate = () => {
    if (this.#layoutRafId !== null) return;

    this.#layoutRafId = requestAnimationFrame(() => {
      this.#layoutRafId = null;
      this.#updateLayout();
    });
  };

  #updateLayout = () => {
    this.#updateScrollbarGutter();
    this.#updateLift();
  };

  /**
   * On desktop the `.page-wrapper` is the scroll container, so its scrollbar sits inside the
   * viewport. Keep a right-aligned button clear of it.
   */
  #updateScrollbarGutter() {
    const container = getScrollContainer();
    const isRoot = container === document.scrollingElement || container === document.documentElement;
    const gutter = isRoot || !(container instanceof HTMLElement) ? 0 : container.offsetWidth - container.clientWidth;

    this.style.setProperty('--pp-stt-gutter', `${Math.max(0, gutter)}px`);
  }

  /**
   * Lifts the button above any visible sticky add-to-cart bar it would otherwise cover.
   */
  #updateLift() {
    let lift = 0;
    const ownRect = this.getBoundingClientRect();

    for (const bar of this.#stickyBars) {
      if (!bar.isConnected || bar.dataset.stuck !== 'true' || bar.offsetHeight === 0) continue;

      const barRect = bar.getBoundingClientRect();
      const overlapsHorizontally =
        barRect.left < ownRect.right + OVERLAP_MARGIN && barRect.right > ownRect.left - OVERLAP_MARGIN;
      if (!overlapsHorizontally) continue;

      const barBottom = Number.parseFloat(getComputedStyle(bar).bottom) || 0;
      lift = Math.max(lift, bar.offsetHeight + barBottom);
    }

    this.style.setProperty('--pp-stt-lift', `${Math.ceil(lift)}px`);
  }

  #scheduleStickyBarWatch = () => {
    requestAnimationFrame(() => this.#watchStickyBars());
  };

  /**
   * Observes Horizon's sticky add-to-cart bar(s): `data-stuck` toggles visibility, and size
   * changes cover variant updates, availability (`display: none`) and breakpoint switches.
   */
  #watchStickyBars() {
    this.#barMutationObserver?.disconnect();
    this.#barResizeObserver?.disconnect();

    this.#stickyBars = Array.from(document.querySelectorAll('sticky-add-to-cart .sticky-add-to-cart__bar')).filter(
      (bar) => bar instanceof HTMLElement
    );

    if (this.#stickyBars.length > 0) {
      this.#barMutationObserver ??= new MutationObserver(this.#scheduleLayoutUpdate);
      this.#barResizeObserver ??= new ResizeObserver(this.#scheduleLayoutUpdate);

      for (const bar of this.#stickyBars) {
        this.#barMutationObserver.observe(bar, { attributes: true, attributeFilter: ['data-stuck'] });
        this.#barResizeObserver.observe(bar);
      }
    }

    this.#scheduleLayoutUpdate();
  }

  /**
   * A setting change re-renders this section while it is selected in the editor,
   * so keep the button revealed for the merchant.
   * @param {Event} event
   */
  #handleSectionLoad = (event) => {
    if (this.#isOwnSectionEvent(event)) {
      this.#editorSelected = true;
      this.#updateVisibility();
    }

    this.#scheduleStickyBarWatch();
  };

  /** @param {Event} event */
  #handleSectionSelect = (event) => {
    if (!this.#isOwnSectionEvent(event)) return;

    this.#editorSelected = true;
    this.#updateVisibility();
  };

  /** @param {Event} event */
  #handleSectionDeselect = (event) => {
    if (!this.#isOwnSectionEvent(event)) return;

    this.#editorSelected = false;
    this.#updateVisibility();
  };

  /**
   * @param {Event} event
   * @returns {boolean}
   */
  #isOwnSectionEvent(event) {
    const detail = event instanceof CustomEvent ? event.detail : null;
    return Boolean(detail?.sectionId) && detail.sectionId === this.dataset.sectionId;
  }
}

if (!customElements.get('pp-scroll-to-top-component')) {
  customElements.define('pp-scroll-to-top-component', ScrollToTopComponent);
}
